/**
 * Test versus control — the comparison the estate actually stores. bata-ui#132 step 5.3.
 *
 * ── WHAT THE CONTROL GROUP IS, FROM THE CONTRACT ─────────────────────────────
 *
 * The one published read that distinguishes a treated arm from a control arm is gate D4's
 * evidence (`GET /exit/d4-evidence`, reader `ExitReader.d4_evidence`, assigned in
 * `bata-platform`'s `wiring.py` on `origin/main` 2026-09-26). Each row is one option × cluster
 * × shadow season and carries BOTH arms side by side:
 *
 *   test     the shadow cadence — `shadow_realised_price`, `shadow_terminal_units`, produced by
 *            the markdown variant `markdown_variant_id`
 *   control  the incumbent — `incumbent_realised_price`, `incumbent_terminal_units`, and the
 *            `incumbent_observed_realised_price` the incumbent actually achieved
 *
 * So the control is **the incumbent pricing on the same option and cluster**, a paired design,
 * and the selector chooses which PAIRS are compared — the shadow season and the market — never
 * which rows are "control". There is no field that assigns a door, a cluster or an option to a
 * held-out control group, and none is invented here: that absence is stated on the screen and
 * is a follow-up on the board, not a gap filled by guessing.
 *
 * ── THE ONE RULE THAT MATTERS ────────────────────────────────────────────────
 *
 * `plan.exit_shadow_comparison` forbids storing a delta while a row is not evaluable, and a
 * blank delta read as zero would publish "the new cadence recovered nothing" — a false negative
 * on the programme's central hypothesis. A not-evaluable row is its own state here, its delta
 * is `null`, and no aggregate over deltas is computed at all: realised prices of different
 * options are not a quantity that averages into anything a planner could act on.
 */

import type { D4EvidenceRow } from '@/api/generated';

import { parseDecimal } from './impact';

/** The address-bar keys this screen's perimeter lives under — ch06 and ch04 spell theirs alike. */
export const SEASON_PARAM = 'season';
export const COUNTRY_PARAM = 'country';

/** The comparison perimeter. `null` is "every one the read returned", stated as such. */
export interface ControlScope {
  readonly season: string | null;
  readonly country: string | null;
}

/** A pure parse of the search string (CLAUDE.md §3.4 rule 4). Blank is absent. */
export function readControlScope(search: string): ControlScope {
  const params = new URLSearchParams(search);
  const read = (name: string): string | null => {
    const value = params.get(name);
    return value !== null && value.trim() !== '' ? value : null;
  };
  return { season: read(SEASON_PARAM), country: read(COUNTRY_PARAM) };
}

/** The values a selector offers: the ones the read returned, distinct and sorted. */
export function perimeterOptions(rows: readonly D4EvidenceRow[]): {
  readonly seasons: readonly string[];
  readonly countries: readonly string[];
} {
  const distinct = (values: readonly string[]): readonly string[] =>
    [...new Set(values.filter((value) => value.trim() !== ''))].sort();
  return {
    seasons: distinct(rows.map((row) => row.season_code)),
    countries: distinct(rows.map((row) => row.country_key)),
  };
}

/** The pairs inside the chosen perimeter. */
export function inPerimeter(rows: readonly D4EvidenceRow[], scope: ControlScope): readonly D4EvidenceRow[] {
  return rows.filter((row) => (
    (scope.season === null || row.season_code === scope.season)
    && (scope.country === null || row.country_key === scope.country)
  ));
}

/** A pair's outcome. Three states, and not-evaluable is not the empty case of the other two. */
export type PairVerdict = 'test-better' | 'test-not-better' | 'not-evaluable';

/** The delta, only when the row is evaluable — `null` otherwise, never `0`. */
export function pairDelta(row: D4EvidenceRow): number | null {
  return row.is_evaluable ? parseDecimal(row.realised_price_delta) : null;
}

/**
 * A positive realised-price delta is the test arm doing better — exactly exit's `d4Verdict`
 * on `/exit/evidence`, restated because a chapter may not import another. The rule is kept
 * identical ON PURPOSE: two screens giving one pair two verdicts is worse than either verdict.
 * Whether terminal stock was held constant is shown beside it as its own column, as exit does.
 */
export function pairVerdict(row: D4EvidenceRow): PairVerdict {
  const delta = pairDelta(row);
  if (delta === null) return 'not-evaluable';
  return delta > 0 ? 'test-better' : 'test-not-better';
}

/** The row's own reason codes, blanks dropped, in the platform's order. */
export function pairReasonCodes(row: D4EvidenceRow): readonly string[] {
  return row.not_evaluable_reason_codes.filter((code) => code.trim() !== '');
}

/** Counts over the perimeter — counts of pairs, the only aggregate this comparison admits. */
export interface ControlStanding {
  readonly pairs: number;
  readonly evaluable: number;
  readonly notEvaluable: number;
  /** Pairs where the test arm did better. `null` while NO pair is evaluable — not zero. */
  readonly testBetter: number | null;
  /** The platform's reasons for the not-evaluable pairs, distinct and sorted. */
  readonly reasonCodes: readonly string[];
}

export function controlStanding(rows: readonly D4EvidenceRow[]): ControlStanding {
  const evaluable = rows.filter((row) => pairDelta(row) !== null);
  return {
    pairs: rows.length,
    evaluable: evaluable.length,
    notEvaluable: rows.length - evaluable.length,
    testBetter: evaluable.length === 0 ? null : evaluable.filter((row) => pairVerdict(row) === 'test-better').length,
    reasonCodes: [...new Set(rows.flatMap(pairReasonCodes))].sort(),
  };
}
